import React, { useState, useEffect, useCallback } from 'react';
import userManagementService from '../../services/userManagementService';
import roleService from '../../services/roleService';
import { useToast } from '../../context/ToastContext';

// ── Badge helpers ─────────────────────────────────────────────────────────────

const ROLE_BADGE = {
  ADMIN:   'bg-purple-100 text-purple-700',
  KAM:     'bg-blue-100 text-blue-700',
  SUPPORT: 'bg-green-100 text-green-700',
};

// ── Create / Edit User Modal ──────────────────────────────────────────────────

const UserModal = ({ open, onClose, onSaved, editUser, roles }) => {
  const { success: toastSuccess, error: toastError } = useToast();

  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [team,         setTeam]         = useState('');
  const [customRoleId, setCustomRoleId] = useState('');
  const [saving,       setSaving]       = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editUser) {
      setName(editUser.name || '');
      setEmail(editUser.email || '');
      setPassword('');
      setTeam(editUser.team || '');
      setCustomRoleId(editUser.customRoleId ? String(editUser.customRoleId) : '');
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setTeam('');
      setCustomRoleId('');
    }
  }, [editUser, open]);

  const handleSave = async () => {
    if (!name.trim()) { toastError('Name is required'); return; }
    if (!editUser && !email.trim()) { toastError('Email is required'); return; }
    if (!editUser && password.length < 6) { toastError('Password must be at least 6 characters'); return; }

    setSaving(true);
    try {
      if (editUser) {
        const payload = {
          name:         name.trim(),
          team:         team.trim() || null,
          unassignRole: customRoleId === '',
          customRoleId: customRoleId ? Number(customRoleId) : null,
        };
        await userManagementService.update(editUser.userId, payload);
        toastSuccess('User updated successfully');
      } else {
        const payload = {
          name:         name.trim(),
          email:        email.trim().toLowerCase(),
          password,
          team:         team.trim() || null,
          customRoleId: customRoleId ? Number(customRoleId) : null,
        };
        await userManagementService.create(payload);
        toastSuccess('User created successfully');
      }
      onSaved();
      onClose();
    } catch (e) {
      toastError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800">
            {editUser ? 'Edit User' : 'Create New User'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Priya Sharma"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Email — only for create */}
          {!editUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="priya@company.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Password — only for create */}
          {!editUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Team */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Team / Department</label>
            <input
              value={team}
              onChange={e => setTeam(e.target.value)}
              placeholder="e.g. Finance, Operations, KAM"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Custom Role */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Custom Role (Permissions)</label>
            <select
              value={customRoleId}
              onChange={e => setCustomRoleId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
            >
              <option value="">— None (Super Admin: all access) —</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Leaving this blank gives full access (super admin). Assign a role to restrict permissions.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-60 flex items-center gap-1.5"
          >
            {saving && (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {editUser ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const UserManagement = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [users,       setUsers]       = useState([]);
  const [roles,       setRoles]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editUser,    setEditUser]    = useState(null);
  const [toggling,    setToggling]    = useState({}); // userId → true

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        userManagementService.getAll(),
        roleService.getRoles(),
      ]);
      setUsers(usersRes.data || []);
      setRoles(rolesRes.data || []);
    } catch {
      toastError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = () => { setEditUser(null); setModalOpen(true); };
  const handleEdit   = (u)  => { setEditUser(u);  setModalOpen(true); };

  const handleToggleActive = async (u) => {
    setToggling(prev => ({ ...prev, [u.userId]: true }));
    try {
      await userManagementService.toggleActive(u.userId);
      toastSuccess(`User ${u.active ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch {
      toastError('Failed to update user status');
    } finally {
      setToggling(prev => ({ ...prev, [u.userId]: false }));
    }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.team?.toLowerCase().includes(q) ||
      u.customRoleName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Create and manage portal users — {users.length} total
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-light transition-colors"
          >
            + Create User
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, team, or role…"
            className="w-full max-w-sm border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-4xl mb-3">👤</p>
                <p className="text-sm font-medium text-gray-500">
                  {search ? 'No users match your search' : 'No users found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Team</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom Role</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map(u => (
                      <tr key={u.userId} className={`hover:bg-gray-50 ${!u.active ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{u.name}</div>
                          <div className="text-xs text-gray-400 font-mono">{u.userId}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{u.email}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{u.team || '—'}</td>
                        <td className="px-4 py-3">
                          {u.customRoleName ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                              {u.customRoleName}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Super Admin</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}>
                            {u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(u)}
                              className="text-xs px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(u)}
                              disabled={!!toggling[u.userId]}
                              className={`text-xs px-3 py-1 border rounded-lg transition-colors disabled:opacity-60 ${
                                u.active
                                  ? 'border-red-200 hover:bg-red-50 text-red-600'
                                  : 'border-green-200 hover:bg-green-50 text-green-700'
                              }`}
                            >
                              {toggling[u.userId] ? (
                                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                u.active ? 'Deactivate' : 'Activate'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <UserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchData}
        editUser={editUser}
        roles={roles}
      />
    </div>
  );
};

export default UserManagement;
